import React, { useEffect, useState, useRef } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Upload, Loader2, Save, FileDown, Database, ScrollText, FileText, Mail } from "lucide-react";
import { toast } from "sonner";
import { APP_VERSION } from "@/lib/version";

export default function SettingsPage() {
  const { loadSettings } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const refs = { client: useRef(null), prestataire: useRef(null), pv: useRef(null), journal: useRef(null) };
  const importRef = useRef(null);
  const [serverVersion, setServerVersion] = useState("");

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/version").then((r) => setServerVersion(r.data.version)).catch(() => setServerVersion("ancienne version")); }, []);

  const upd = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const updParty = (k, v) => setS((p) => ({ ...p, parties: { ...(p.parties || {}), [k]: v } }));

  const uploadTo = async (field, file) => {
    if (!file) return;
    setBusy(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd(field, data.storage_path);
      toast.success("Fichier téléversé (pensez à Enregistrer)");
    } catch (e) {
      toast.error("Échec du téléversement");
    } finally {
      setBusy("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      await loadSettings();
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const downloadBlob = (data, filename, type) => {
    const url = URL.createObjectURL(new Blob([data], { type }));
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadBlank = async (type) => {
    setBusy("blank-" + type);
    try {
      const res = await api.get(`/blank-pdf?type=${type}`, { responseType: "blob" });
      downloadBlob(res.data, (type === "pv_liberation" ? "PV_liberation" : "Journal_chantier") + "_VIERGE.pdf", "application/pdf");
    } catch (e) { toast.error("Échec du téléchargement"); } finally { setBusy(""); }
  };

  const exportBackup = async () => {
    setBusy("export");
    try {
      const res = await api.get("/backup/export");
      downloadBlob(JSON.stringify(res.data, null, 2), `sauvegarde_ir_ingenierie_${new Date().toISOString().slice(0,10)}.json`, "application/json");
      toast.success("Sauvegarde exportée");
    } catch (e) { toast.error("Échec de l'export"); } finally { setBusy(""); }
  };

  const importBackup = async (file) => {
    if (!file) return;
    if (!window.confirm("Importer cette sauvegarde ? Les bons d'intervention actuels seront remplacés.")) return;
    setBusy("import");
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const { data } = await api.post("/backup/import", json);
      toast.success(`Sauvegarde importée (${data.interventions} bons, ${data.users} utilisateurs)`);
      const r = await api.get("/settings"); setS(r.data); await loadSettings();
    } catch (e) { toast.error("Fichier de sauvegarde invalide"); } finally { setBusy(""); }
  };

  if (!s) return <div className="py-16 text-center text-sm text-slate-400">Chargement...</div>;

  const LogoSlot = ({ field, label, hint }) => (
    <div className="flex items-center gap-4">
      <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
        {s[field] ? <img src={fileUrl(s[field])} alt={label} className="h-full w-full object-contain p-1" /> : <Building2 className="h-8 w-8 text-slate-300" />}
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{label}</p>
        <input ref={refs[field === "client_logo_path" ? "client" : "prestataire"]} type="file" accept="image/*" hidden
          data-testid={`${field}-input`} onChange={(e) => uploadTo(field, e.target.files?.[0])} />
        <Button onClick={() => refs[field === "client_logo_path" ? "client" : "prestataire"].current?.click()} disabled={busy === field}
          variant="outline" className="mt-2 h-10 gap-2" data-testid={`upload-${field}-btn`}>
          {busy === field ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Téléverser
        </Button>
        <p className="mt-1 text-xs text-slate-400">{hint}</p>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-4xl">Paramètres</h1>
        <p className="mt-1 text-sm text-slate-500">Logos, valeurs par défaut, sauvegarde et modèles vierges.</p>
        <p className="mt-2 inline-flex flex-wrap gap-3 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300" data-testid="app-version">
          <span data-testid="app-version-front">Version appli : {APP_VERSION}</span>
          <span data-testid="app-version-server">Serveur : {serverVersion || "…"}</span>
        </p>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
        <h2 className="flex items-center gap-2 font-head text-lg font-bold text-slate-900 dark:text-white"><Building2 className="h-5 w-5 text-amber-500" /> Logos de l'en-tête PDF</h2>
        <div className="mt-4 grid gap-6 sm:grid-cols-2">
          <LogoSlot field="client_logo_path" label="Logo CLIENT (haut gauche)" hint="Apparaît en haut à gauche du PDF." />
          <LogoSlot field="prestataire_logo_path" label="Logo PRESTATAIRE (haut droite)" hint="Apparaît en haut à droite du PDF." />
        </div>
        <div className="mt-5">
          <Label className="text-xs font-semibold text-slate-500">Nom de l'entreprise</Label>
          <Input value={s.company_name || ""} onChange={(e) => upd("company_name", e.target.value)} data-testid="company-name" className="mt-1 h-11" placeholder="IR INGENIERIE" />
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
        <h2 className="font-head text-lg font-bold text-slate-900 dark:text-white">Valeurs par défaut des trames</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div><Label className="text-xs font-semibold text-slate-500">Autoroute / Projet</Label><Input value={s.default_autoroute || ""} onChange={(e) => upd("default_autoroute", e.target.value)} data-testid="default-autoroute" className="mt-1 h-11" /></div>
          <div><Label className="text-xs font-semibold text-slate-500">Section</Label><Input value={s.default_section || ""} onChange={(e) => upd("default_section", e.target.value)} data-testid="default-section" className="mt-1 h-11" /></div>
          <div className="sm:col-span-2"><Label className="text-xs font-semibold text-slate-500">Activité / Objet</Label><Input value={s.default_activite || ""} onChange={(e) => upd("default_activite", e.target.value)} data-testid="default-activite" className="mt-1 h-11" /></div>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div><Label className="text-xs font-semibold text-slate-500">L'Entreprise</Label><Input value={s.parties?.entreprise || ""} onChange={(e) => updParty("entreprise", e.target.value)} data-testid="party-entreprise" className="mt-1 h-11" /></div>
          <div><Label className="text-xs font-semibold text-slate-500">IR Ingénierie</Label><Input value={s.parties?.iri || ""} onChange={(e) => updParty("iri", e.target.value)} data-testid="party-iri" className="mt-1 h-11" /></div>
          <div><Label className="text-xs font-semibold text-slate-500">District</Label><Input value={s.parties?.district || ""} onChange={(e) => updParty("district", e.target.value)} data-testid="party-district" className="mt-1 h-11" /></div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
        <h2 className="font-head text-lg font-bold text-slate-900 dark:text-white">Modèles vierges (PDF)</h2>
        <p className="mt-1 text-sm text-slate-500">Téléchargez un modèle vierge en cas de besoin, ou téléversez vos propres trames de référence.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {[
            { type: "pv_liberation", field: "blank_pv_path", label: "PV de libération", icon: ScrollText, r: "pv" },
            { type: "journal_chantier", field: "blank_journal_path", label: "Journal de chantier", icon: FileText, r: "journal" },
          ].map((b) => (
            <div key={b.type} className="rounded-xl border border-slate-100 p-3 dark:border-slate-800">
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200"><b.icon className="h-4 w-4 text-amber-500" /> {b.label}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button onClick={() => downloadBlank(b.type)} disabled={busy === "blank-" + b.type} variant="outline" className="h-9 gap-1.5" data-testid={`download-blank-${b.type}`}>
                  {busy === "blank-" + b.type ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Vierge généré
                </Button>
                <input ref={refs[b.r]} type="file" accept="application/pdf" hidden data-testid={`${b.field}-input`} onChange={(e) => uploadTo(b.field, e.target.files?.[0])} />
                <Button onClick={() => refs[b.r].current?.click()} disabled={busy === b.field} variant="outline" className="h-9 gap-1.5" data-testid={`upload-${b.field}-btn`}>
                  {busy === b.field ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Téléverser
                </Button>
                {s[b.field] && <a href={fileUrl(s[b.field])} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 px-3 text-sm text-blue-600 dark:border-slate-700" data-testid={`view-${b.field}`}><FileDown className="h-4 w-4" /> Mon modèle</a>}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
        <h2 className="flex items-center gap-2 font-head text-lg font-bold text-slate-900 dark:text-white"><Database className="h-5 w-5 text-amber-500" /> Sauvegarde</h2>
        <p className="mt-1 text-sm text-slate-500">Exportez toutes les données (utilisateurs, paramètres, bons) ou restaurez une sauvegarde.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={exportBackup} disabled={busy === "export"} className="h-11 gap-2 bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900" data-testid="export-backup-btn">
            {busy === "export" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Exporter la sauvegarde
          </Button>
          <input ref={importRef} type="file" accept="application/json,.json" hidden data-testid="import-backup-input" onChange={(e) => importBackup(e.target.files?.[0])} />
          <Button onClick={() => importRef.current?.click()} disabled={busy === "import"} variant="outline" className="h-11 gap-2" data-testid="import-backup-btn">
            {busy === "import" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importer une sauvegarde
          </Button>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-head text-lg font-bold text-slate-900 dark:text-white"><Mail className="h-5 w-5 text-amber-500" /> Mail de bienvenue</h2>
          <label className="flex cursor-pointer items-center gap-2">
            <input type="checkbox" checked={!!s.welcome_email_enabled} onChange={(e) => upd("welcome_email_enabled", e.target.checked)}
              data-testid="welcome-email-enabled" className="h-5 w-5 accent-amber-500" />
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{s.welcome_email_enabled ? "Activé" : "Désactivé"}</span>
          </label>
        </div>
        <p className="mt-1 text-sm text-slate-500">Envoyé automatiquement à chaque création d'un utilisateur. Variables disponibles : <span className="font-mono text-xs">{"{nom}"}</span>, <span className="font-mono text-xs">{"{email}"}</span>, <span className="font-mono text-xs">{"{mot_de_passe}"}</span>, <span className="font-mono text-xs">{"{lien}"}</span>.</p>
        <div className="mt-4 space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-500">Sujet</Label>
            <Input value={s.welcome_email_subject || ""} onChange={(e) => upd("welcome_email_subject", e.target.value)} data-testid="welcome-email-subject" className="mt-1 h-11" placeholder="Bienvenue sur IR INGENIERIE — vos accès" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500">Message</Label>
            <textarea value={s.welcome_email_body || ""} onChange={(e) => upd("welcome_email_body", e.target.value)} data-testid="welcome-email-body" rows={9}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed text-slate-800 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100" />
            <p className="mt-1 text-xs text-slate-400">Un bouton « Se connecter » menant à l'application est automatiquement ajouté sous votre message.</p>
          </div>
        </div>
      </section>

      <div className="flex justify-end pb-10">
        <Button onClick={save} disabled={saving} data-testid="save-settings-btn" className="h-11 gap-2 bg-amber-500 font-semibold text-slate-900 hover:bg-amber-400">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
        </Button>
      </div>
    </div>
  );
}
